import { useLazyQuery, useQuery } from "@apollo/client"
import moment from "moment"
import { useEffect, useRef, useState } from "react"
import { Box, Card, Flex, Text } from "rebass/styled-components"
import { Area, ComposedChart, ResponsiveContainer, Scatter, Tooltip, TooltipProps, XAxis, YAxis } from "recharts"
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent"
import styled from "styled-components"
import { Skeleton } from "../../components/loadingSkeleton/Skeleton"
import RadioButton from "../../components/RadioButton"
import { GET_LAB_CHART_VALUES } from "../../graphql/queries"
import { IGetLabChartValuesInput, IGetLabChartValuesOutput, ILabChartValue } from "../../graphql/types/queries/symptoms"
import { useIsVisible } from "../../hooks/useIsVisible"

const ToolTip = styled(Box)`
  padding: 0.8rem;
  border-radius: 10px;
  box-shadow: 3px 3px 3px rgba(0, 0, 0, 0.3);
`

const ContainerMobile = styled(Flex)`
  width: 100%;
  max-width: 100%;
    padding: 0 20px;
    overflow-x: hidden;
`;

const PeriodSelectorItem = styled(Flex) <{ isSelected: boolean }>`
  padding: 0 8px;
  border-radius: 24px;
  margin-left: 4px;
  margin-right: 4px;
  justify-content: center;
  align-items: center;
  background: ${(props) => (props.isSelected ? '#354E79' : 'transparent')};
  color: ${(props) => (!props.isSelected ? '#354E79' : 'white')};
`;

const PeriodSelector = ({ period, onClick, isSelected, isDisabled }: { period: string, onClick: () => void, isSelected: boolean, isDisabled: boolean }) => {
  if (!!isDisabled) return null;

  return (
    <PeriodSelectorItem isSelected={isSelected} onClick={onClick}>
      {period}
    </PeriodSelectorItem>
  );
};

const CustomizedTick = ({ x, y, payload }: any) => {
  return <text style={{ fontSize: "10px", fill: '#A3A3A3' }} x={x} y={y} textAnchor="top" dominantBaseline="hanging">
    {moment(new Date(payload.value * 100 / 100)).format('MMM DD YYYY')}
  </text>
}
const CustomizedTick2 = ({ x, y, payload }: any) => {
  // console.log(props)
  return <text style={{ fontSize: "10px", fill: '#A3A3A3' }} x={x - 24} y={y - 6} textAnchor="top" dominantBaseline="hanging">
    {payload.value}
  </text>
}

const getValues = (output: IGetLabChartValuesOutput, selectedMarker: 'TSH' |
  'ft4' |
  'ft3' |
  'tpo' |
  'rt3' |
  'vitD') => {
  const data: Array<{ x: string; y: null | number }> = []
  const labTestsPoints: { x: string; y: number | null; isPaloma?: boolean, unit: string; isSelfEntered?: boolean, fill?: string }[] = []

  let lastAvg2: null | number = null
  let max = 1
  // useEffect(()=>{

  let vals: Array<ILabChartValue | null> = []
  let unit: string
  switch (selectedMarker) {
    case 'tpo':
      vals = output.healthCoachGetLabChartValues.tpo || []
      unit = 'IU/mL'
      break;
    case 'ft3':
      vals = output.healthCoachGetLabChartValues.ft3 || []
      unit = 'pg/mL'
      break;
    case 'ft4':
      vals = output.healthCoachGetLabChartValues.ft4 || []
      unit = 'ng/dL'
      break;
    case 'rt3':
      vals = output.healthCoachGetLabChartValues.rT3 || []
      unit = 'IU/mL'
      break;
    case 'vitD':
      vals = output.healthCoachGetLabChartValues.vitD || []
      unit = 'ng/L'
      break;
    case 'TSH':
    default:
      vals = output.healthCoachGetLabChartValues.tsh || []
      unit = 'uU/mL'
      break;
  }
  const sorted = (JSON.parse(JSON.stringify(vals)) as Array<ILabChartValue | null>).sort((a, b) => {
    if (!a || !a.date) return -1;
    if (!b || !b.date) return 1;
    let dta = a.date.indexOf('/') > -1 ? moment(a.date).format('x') : a.date
    let dtB = b.date.indexOf('/') > -1 ? moment(b.date).format('x') : b.date
    if (dta < dtB) return -1;
    return 0
  })
  console.log({ sorted })
  for (let index = 0; index < sorted.length; index++) {
    const element = sorted[index];
    if (!element) continue
    if (element.value) {
      lastAvg2 = (element.value !== null && element.value !== undefined ? element.value : 0) / 100
    }
    data.push({
      x: element.date,
      y: element.value !== null && element.value !== undefined ? element.value : null
    })
    max = Math.max(lastAvg2 || 1, max)

    if (element.value) {
      labTestsPoints.push({
        x: element.date.indexOf('-') > -1 || element.date.indexOf('/') > -1 ? moment(element.date).format('x') : element.date,
        y: (element.value !== null && element.value !== undefined ? element.value : 0) / 100,
        isPaloma: element.isPaloma,
        unit: unit,
        isSelfEntered: element.isSelfEntered,
        fill: element.isSelfEntered ? '#f1c40f' : 'white'
      })
    }
  }

  return { labTestsPoints, max, unit }
}

const CustomizedTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (active) {
    return (
      <ToolTip style={{ background: !!payload && payload.length > 0 && payload[0].payload.isSelfEntered ? '#f1c40f' : '#4E658A' }}>
        <Flex flexDirection={'column'}>
          {!!payload && payload.length > 0 && payload[0].payload.isSelfEntered && (
            <Text fontWeight={500} color='white'>Self Entered</Text>
          )}
          <Text fontWeight={300} color='white'>{!!payload && payload.length > 0 ? moment(new Date(payload[0].payload.x * 100 / 100)).format('MMM DD YYYY') : ''}</Text>
          <Flex alignItems={'baseline'}>

            <Text fontWeight={500} color='white'>{!!payload && payload.length > 0 && (!!payload[0].payload.y || payload[0].payload.y === 0) ? payload[0].payload.y : ''}</Text>
            <Text fontWeight={300} fontSize='10px' color='white'>{!!payload && payload.length > 0 && (!!payload[0].payload.unit) ? payload[0].payload.unit : ''}</Text>
          </Flex>
        </Flex>

      </ToolTip>
    );
  }

  return null;
};

const Labs = ({ id, isMinimized }: { id: string, isMinimized?: boolean }) => {
  const [selected, setSelected] = useState<
    'TSH' |
    'ft4' |
    'ft3' |
    'tpo' |
    'rt3' |
    'vitD'>('TSH')
  const [values, setValues] = useState<Array<{
    x: string;
    y: number | null;
    isPaloma?: boolean | undefined;
  }> | null>(null)
  const [max, setMax] = useState<number>(1)
  const [, setUnit] = useState<string>('')
  const ref = useRef<any>();
  const isVisible = useIsVisible(ref);
  const [load, { data, loading, error }] = useLazyQuery<
    IGetLabChartValuesOutput, IGetLabChartValuesInput>(GET_LAB_CHART_VALUES, {
      variables: {
        patientId: id
      }
    })

  useEffect(() => {
    if (!!isVisible) {
      load()
    }
  }, [isVisible])
  // const { data, loading } = useQuery<
  //   IGetLabChartValuesOutput, IGetLabChartValuesInput>(GET_LAB_CHART_VALUES, {
  //     variables: {
  //       patientId: id
  //     }
  //   })

  useEffect(() => {
    if (loading || !data) {
      setValues(null)
      return
    }
    const vls = getValues(data, selected)
    setValues(vls.labTestsPoints)
    setUnit(vls.unit)
    setMax(vls.max)
  }, [data, loading, selected])

  return (
    <Flex ref={ref} flexDirection={'column'} width='100%' padding={'20px'}>
      {!loading &&
        (!data?.healthCoachGetLabChartValues.tsh || data?.healthCoachGetLabChartValues.tsh.length < 1) &&
        (!data?.healthCoachGetLabChartValues.ft4 || data?.healthCoachGetLabChartValues.ft4.length < 1) &&
        (!data?.healthCoachGetLabChartValues.ft3 || data?.healthCoachGetLabChartValues.ft3.length < 1) &&
        (!data?.healthCoachGetLabChartValues.tpo || data?.healthCoachGetLabChartValues.tpo.length < 1) &&
        (!data?.healthCoachGetLabChartValues.rT3 || data?.healthCoachGetLabChartValues.rT3.length < 1) &&
        (!data?.healthCoachGetLabChartValues.vitD || data?.healthCoachGetLabChartValues.vitD.length < 1) ?
        (<>
          <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial',  }} paddingY={'12px'} mb={4}>
            <Flex>
              <Flex flexDirection={'column'} flex={1}>
                <Text fontWeight={600} fontSize='18px' m={'18px 28px'}>Labs Charts</Text>
                <Box flex={1}>
                  <Text textAlign={'center'} style={{ opacity: 0.3 }} mb={4}>No lab results</Text>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </>
        ) : (
          <>

            <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial',  }} paddingY={'12px'} paddingBottom={isMinimized?'0px':'12px'} mb={isMinimized?0:4}>
              <Flex flexDirection={isMinimized ? 'column' : 'row'}>

                <Flex flexDirection={'column'} flex={1}>
                  <Text fontWeight={600} fontSize='18px' m={'18px 28px'}>Labs</Text>
                  <Box flex={1} height={isMinimized?'150px':''} minHeight={isMinimized?'150px':''} id='rcWrap'>
                    {!!loading ? (
                      <Skeleton width={'calc(100% - 24px)'} height='calc(100% - 12px)' style={{ borderRadius: '0 0 30px 30px', margin: '0 12px 12px 12px' }} />
                    ) : (
                      <ResponsiveContainer width="100%" height={"100%"}>
                        <ComposedChart
                          data={values || []}
                          margin={{
                            top: 10,
                            right: 0,
                            left: 0,
                            bottom: 0,
                          }}
                        >
                          <defs>
                            <linearGradient id="colorUvLabsTSH" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="rgb(30, 70, 212) " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="rgb(30, 70, 212) " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsft4" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#344F7A " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#344F7A " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsft3" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#BE05C1 " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#BE05C1 " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabstpo" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#F7BE2B " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#F7BE2B " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsrt3" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#9B4A01 " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#9B4A01 " stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="colorUvLabsvitD" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#000000 " stopOpacity={0.55} />
                              <stop offset="95%" stopColor="#000000 " stopOpacity={0.05} />
                            </linearGradient>
                          </defs>
                          <Tooltip content={<CustomizedTooltip />} />
                          <XAxis dataKey="x"
                            interval={0}
                            tick={CustomizedTick} />
                          <YAxis
                            tick={CustomizedTick2}
                            // ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                            interval={0}
                            domain={[0, Math.ceil(max * 1.1)]}
                          />
                          <Area type="monotone" dataKey="y" stroke={
                            selected === 'TSH' ? "#1E46D4" : selected === 'ft4' ? '#344F7A' : selected === 'ft3' ? '#BE05C1' : selected === 'tpo' ? '#F7BE2B' :
                              selected === 'rt3' ? '#9B4A01' : selected === 'vitD' ? '#000000' : '#1E46D4'
                          } fill={`url(#colorUvLabs${selected})`} />
                          <Scatter dataKey={'y'} stroke={
                            selected === 'TSH' ? "#1E46D4" : selected === 'ft4' ? '#344F7A' : selected === 'ft3' ? '#BE05C1' : selected === 'tpo' ? '#F7BE2B' :
                              selected === 'rt3' ? '#9B4A01' : selected === 'vitD' ? '#000000' : '#1E46D4'
                          } strokeWidth={2} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}

                  </Box>
                </Flex>

                  <Box height={isMinimized ? '1px' : '236px'} width={isMinimized ? '100%' : '1px'} backgroundColor={'rgba(52, 79, 122, 0.41)'}>

                  </Box>

                <Flex flexDirection={isMinimized?'row':'column'} flexWrap={'wrap'} padding='24px 24px' justifyContent={'center'}>
                  <RadioButton disabled={!data?.healthCoachGetLabChartValues.tsh || data?.healthCoachGetLabChartValues.tsh.length < 1 || data?.healthCoachGetLabChartValues.tsh.filter(x => x.value !== null).length < 1} label="TSH" value={selected === 'TSH'} onChange={() => { setSelected('TSH') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.healthCoachGetLabChartValues.ft4 || data?.healthCoachGetLabChartValues.ft4.length < 1 || data?.healthCoachGetLabChartValues.ft4.filter(x => x.value !== null).length < 1} label="Free T4" value={selected === 'ft4'} onChange={() => { setSelected('ft4') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.healthCoachGetLabChartValues.ft3 || data?.healthCoachGetLabChartValues.ft3.length < 1 || data?.healthCoachGetLabChartValues.ft3.filter(x => x.value !== null).length < 1} label="Free T3" value={selected === 'ft3'} onChange={() => { setSelected('ft3') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.healthCoachGetLabChartValues.tpo || data?.healthCoachGetLabChartValues.tpo.length < 1 || data?.healthCoachGetLabChartValues.tpo.filter(x => x.value !== null).length < 1} label="TPO antibodies" value={selected === 'tpo'} onChange={() => { setSelected('tpo') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.healthCoachGetLabChartValues.rT3 || data?.healthCoachGetLabChartValues.rT3.length < 1 || data?.healthCoachGetLabChartValues.rT3.filter(x => x.value !== null).length < 1} label="Reverse T3" value={selected === 'rt3'} onChange={() => { setSelected('rt3') }} />
                  {isMinimized && <Box ml={3} />}
                  <RadioButton disabled={!data?.healthCoachGetLabChartValues.vitD || data?.healthCoachGetLabChartValues.vitD.length < 1 || data?.healthCoachGetLabChartValues.vitD.filter(x => x.value !== null).length < 1} label="Vitamin D" value={selected === 'vitD'} onChange={() => { setSelected('vitD') }} />
                </Flex>
              </Flex>
            </Flex>
            <ContainerMobile flexDirection={'column'} display={['initial', 'none', 'none']} height={['400px', 0, 0]}>
              <Text mb="20px">Lab Results</Text>
              <Flex flexWrap={'wrap'} style={{ justifyContent: 'center', marginBottom: '24px' }}>
                <PeriodSelector
                  isDisabled={!data || !data.healthCoachGetLabChartValues || !data.healthCoachGetLabChartValues.tsh || data.healthCoachGetLabChartValues.tsh.length < 1 || data.healthCoachGetLabChartValues.tsh.filter((x) => x.value !== null).length < 1}
                  period="TSH"
                  isSelected={selected === 'TSH'}
                  onClick={() => {
                    setSelected('TSH');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.healthCoachGetLabChartValues || !data.healthCoachGetLabChartValues.ft4 || data.healthCoachGetLabChartValues.ft4.length < 1 || data.healthCoachGetLabChartValues.ft4.filter((x) => x.value !== null).length < 1}
                  period="fT4"
                  isSelected={selected === 'ft4'}
                  onClick={() => {
                    setSelected('ft4');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.healthCoachGetLabChartValues || !data.healthCoachGetLabChartValues.ft3 || data.healthCoachGetLabChartValues.ft3.length < 1 || data.healthCoachGetLabChartValues.ft3.filter((x) => x.value !== null).length < 1}
                  period="fT3"
                  isSelected={selected === 'ft3'}
                  onClick={() => {
                    setSelected('ft3');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.healthCoachGetLabChartValues || !data.healthCoachGetLabChartValues.tpo || data.healthCoachGetLabChartValues.tpo.length < 1 || data.healthCoachGetLabChartValues.tpo.filter((x) => x.value !== null).length < 1}
                  period="TPO"
                  isSelected={selected === 'tpo'}
                  onClick={() => {
                    setSelected('tpo');
                  }}
                />
                <PeriodSelector
                  isDisabled={!data || !data.healthCoachGetLabChartValues || !data.healthCoachGetLabChartValues.rT3 || data.healthCoachGetLabChartValues.rT3.length < 1 || data.healthCoachGetLabChartValues.rT3.filter((x) => x.value !== null).length < 1}
                  period="rT3"
                  isSelected={selected === 'rt3'}
                  onClick={() => {
                    setSelected('rt3');
                  }}
                />
                <PeriodSelector
                  isDisabled={
                    !data || !data.healthCoachGetLabChartValues || !data.healthCoachGetLabChartValues.vitD || data.healthCoachGetLabChartValues.vitD.length < 1 || data.healthCoachGetLabChartValues.vitD.filter((x) => x.value !== null).length < 1
                  }
                  period="vit. D"
                  isSelected={selected === 'vitD'}
                  onClick={() => {
                    setSelected('vitD');
                  }}
                />
              </Flex>
              <Flex flex={1} marginX='-30px'>

                <ResponsiveContainer width="100%" height="97%">
                  <ComposedChart
                    data={values || []}
                    margin={{
                      top: 10,
                      right: 0,
                      left: 0,
                      bottom: 0,
                    }}

                  >
                    <defs>
                      <linearGradient id="colorUvLabsTSH" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="rgb(30, 70, 212) " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="rgb(30, 70, 212) " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsft4" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#344F7A " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#344F7A " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsft3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#BE05C1 " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#BE05C1 " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabstpo" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F7BE2B " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#F7BE2B " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsrt3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9B4A01 " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#9B4A01 " stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorUvLabsvitD" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#000000 " stopOpacity={0.55} />
                        <stop offset="95%" stopColor="#000000 " stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <Tooltip content={<CustomizedTooltip />} />
                    <XAxis axisLine={false} dataKey="x" tick={CustomizedTick} padding={{
                      left: 16,
                      right: 16
                    }} />
                    <YAxis hide />
                    <Area
                      type="monotone"
                      dataKey="y"
                      strokeWidth={'3px'}

                      stroke={
                        selected === 'TSH'
                          ? '#1E46D4'
                          : selected === 'ft4'
                            ? '#344F7A'
                            : selected === 'ft3'
                              ? '#BE05C1'
                              : selected === 'tpo'
                                ? '#F7BE2B'
                                : selected === 'rt3'
                                  ? '#9B4A01'
                                  : selected === 'vitD'
                                    ? '#000000'
                                    : '#1E46D4'
                      }
                      fill={`transparent`}
                    />
                    <Scatter
                      dataKey={'y'}
                      stroke={
                        selected === 'TSH'
                          ? '#1E46D4'
                          : selected === 'ft4'
                            ? '#344F7A'
                            : selected === 'ft3'
                              ? '#BE05C1'
                              : selected === 'tpo'
                                ? '#F7BE2B'
                                : selected === 'rt3'
                                  ? '#9B4A01'
                                  : selected === 'vitD'
                                    ? '#000000'
                                    : '#1E46D4'
                      }
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Flex>
            </ContainerMobile>
          </>
        )}
    </Flex>
  )
}

export default Labs